import Layout from "../../components/layoutConfig/layout";
import { useState, useEffect, ChangeEvent, FormEvent } from "react";
import { useRef } from "react";
import { motion } from "framer-motion";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import {
  FiBox,
  FiList,
  FiPlusCircle,
  FiCheckCircle,
  FiTrash2,
  FiEdit,
  FiMap,
  FiGlobe,
  FiSearch,
  FiLayers,
  FiX,
} from "react-icons/fi";
import { setupAPIclient } from "../../services/setupAPIclient";
import LocalStorage from "../../helpers/localstorage";
import GuidedTutorial from "../../components/common/GuidedTutorial";

interface Region {
  id: number;
  name: string;
}

interface Unit {
  id: number;
  name: string;
  management_location: Location[];
}

interface Location {
  id: number;
  name: string;
}

interface Company {
  company_id: number;
  name_company: string;
  region_id: number;
  region_name: string;
  unit_id: number;
  unit_name: string;
}

function CompanyPage() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const ApiClient = setupAPIclient(token);

  const [data, setData] = useState<Company[]>([]);
  const [pending, setPending] = useState<boolean>(false);
  const [filterText, setFilterText] = useState("");
  const [editId, setEditId] = useState<number | null>(null);

  const [regions, setRegions] = useState<Region[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  const [form, setForm] = useState({
    name_company: "",
    region_id: "",
    unit_id: "",
  });
  const nameRef = useRef<HTMLInputElement>(null);
  const regionRef = useRef<HTMLSelectElement>(null);
  const unitRef = useRef<HTMLSelectElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const iconColor = "rgb(93, 173, 226)";

  const handleGetCompanies = async () => {
    try {
      setPending(true);
      const response = await ApiClient.post("/management/get/company");
      setData(response.data.success || []);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar empresas");
    } finally {
      setPending(false);
    }
  };

  const handleGetRegions = async () => {
    try {
      const response = await ApiClient.post("/management/get/region");
      setRegions(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleGetUnitsByRegion = async (regionId: number) => {
    try {
      const response = await ApiClient.post("/management/get/association/region", { regionId });
      setUnits(response.data.management_unit || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleGetLocationsByUnit = async (unitId: number) => {
    try {
      const response = await ApiClient.post("/management/get/association/localByUnit", { unitId });
      setLocations(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    handleGetRegions();
    handleGetCompanies();
  }, []);

  const handleRegionChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const regionId = e.target.value;
    setForm({ ...form, region_id: regionId, unit_id: "" });
    setLocations([]);
    if (regionId) handleGetUnitsByRegion(Number(regionId));
  };

  const handleUnitChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const unitId = e.target.value;
    setForm({ ...form, unit_id: unitId });
    if (unitId) handleGetLocationsByUnit(Number(unitId));
  };

  const handleCreateOrUpdate = async (e: FormEvent) => {
    e.preventDefault();
    const { name_company } = form;

    if (!name_company) {
      toast.warning("Preencha o campo de empresa");
      return;
    }

    try {
      if (editId) {
        await ApiClient.post("/management/update/company", {
          id: editId,
          ...form,
        });
        toast.success("Empresa atualizada com sucesso!");
      } else {
        await ApiClient.post("/management/create/company", form);
        toast.success("Empresa criada com sucesso!");
      }

      setForm({ name_company: "", region_id: "", unit_id: "" });
      setEditId(null);
      setUnits([]);
      setLocations([]);
      handleGetCompanies();
      window.dispatchEvent(new Event("management:onboarding-updated"));
    } catch (error) {
      console.error(error);
      toast.error("Erro ao salvar empresa");
    }
  };

  const handleEdit = (company: Company) => {
    setEditId(company.company_id);
    setForm({
      name_company: company.name_company,
      region_id: String(company.region_id),
      unit_id: String(company.unit_id),
    });
    handleGetUnitsByRegion(company.region_id);
    handleGetLocationsByUnit(company.unit_id);
  };

  const handleDelete = async (id: number) => {
    toast.info("Excluindo empresa...");
    try {
      await ApiClient.post("/management/delete/company", { id });
      toast.success("Empresa excluída com sucesso!");
      handleGetCompanies();
      window.dispatchEvent(new Event("management:onboarding-updated"));
    } catch (error) {
      console.error(error);
      toast.error("Erro ao excluir empresa");
    }
  };

  const filteredItems = data.filter((item) => item.name_company.toLowerCase().includes(filterText.toLowerCase()));

  const tableColumns: TableColumn<Company>[] = [
      { name: "ID", selector: (row) => row.company_id, sortable: true, width: "90px" },
      {
        name: "Empresa",
        selector: (row) => row.name_company,
        sortable: true,
        cell: (row) => (
          <div className="d-flex align-items-center gap-2 fw-semibold">
            <FiBox color={iconColor} /> {row.name_company}
          </div>
        ),
      },
      {
        name: "Região",
        selector: (row) => row.region_name,
        cell: (row) => (
          <div className="d-flex align-items-center gap-2">
            <FiGlobe color={iconColor} /> {row.region_name}
          </div>
        ),
      },
      {
        name: "Unidade",
        selector: (row) => row.unit_name,
        cell: (row) => (
          <div className="d-flex align-items-center gap-2">
            <FiMap color={iconColor} /> {row.unit_name}
          </div>
        ),
      },
      {
        name: "Ações",
        width: "130px",
        cell: (row) => (
          <div className="d-flex gap-2">
            <motion.button
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.95 }}
              className="btn btn-sm modern-primary-btn"
              onClick={() => handleEdit(row)}
            >
              <FiEdit />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.95 }}
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDelete(row.company_id)}
            >
              <FiTrash2 />
            </motion.button>
          </div>
        ),
      },
  ];

  return (
    <Layout>
      <GuidedTutorial
        storageKey="management_tutorial_company_v1"
        enabled={localStorage.getStep() === "4"}
        nextSystemStep="5"
        steps={[
          { targetRef: nameRef as any, title: "", description: "Defina o nome da empresa que será vinculada aos contratos." },
          { targetRef: regionRef as any, title: "", description: "Escolha a região da empresa para organizar a estrutura." },
          { targetRef: unitRef as any, title: "", description: "Se desejar, associe também uma unidade já cadastrada." },
          { targetRef: submitRef as any, title: "", description: "Clique em cadastrar para concluir a etapa de empresa." },
          { targetRef: tableRef as any, title: "", description: "Aqui você consulta, edita e exclui empresas já registradas." },
        ]}
      />
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="d-flex flex-wrap gap-3 mb-4">
          <div className="modern-card p-4 flex-grow-1">
            <h4 className="d-flex align-items-center gap-2 mb-2">
              <FiLayers color={iconColor} size={22} /> Configuração de Empresas
            </h4>
            <p className="mb-0" style={{ color: "#64748b" }}>
              Cadastre empresas e vincule com região e unidade mantendo rastreabilidade operacional.
            </p>
          </div>

          <div className="modern-card p-4" style={{ minWidth: 210 }}>
            <div className="d-flex align-items-center gap-2">
              <FiList color={iconColor} size={22} />
              <div>
                <div style={{ color: "#64748b", fontSize: 13 }}>Total de Empresas</div>
                <strong style={{ fontSize: 28 }}>{data.length}</strong>
              </div>
            </div>
          </div>
        </div>

        <motion.div
          className="modern-card p-4 mb-4"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.24 }}
        >
          <h5 className="mb-3 d-flex align-items-center gap-2">
            <FiPlusCircle color={iconColor} /> {editId ? "Editar Empresa" : "Nova Empresa"}
          </h5>

          <form onSubmit={handleCreateOrUpdate}>
            <div className="row align-items-end g-3">
              <div className="col-md-4">
                <label className="form-label">Nome da Empresa</label>
                <input
                  ref={nameRef}
                  type="text"
                  className="form-control"
                  placeholder="Digite o nome da empresa"
                  value={form.name_company}
                  onChange={(e) => setForm({ ...form, name_company: e.target.value })}
                />
              </div>

              <div className="col-md-3">
                <label className="form-label">Região</label>
                <select ref={regionRef} className="form-select" value={form.region_id} onChange={handleRegionChange}>
                  <option value="">Selecione...</option>
                  {regions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Unidade</label>
                <select ref={unitRef} className="form-select" value={form.unit_id} onChange={handleUnitChange} disabled={!units.length}>
                  <option value="">Selecione...</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3 d-flex gap-2">
                <motion.button ref={submitRef} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="btn modern-primary-btn flex-fill">
                  <FiCheckCircle className="me-1" /> {editId ? "Salvar" : "Cadastrar"}
                </motion.button>
                {editId && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setEditId(null);
                      setForm({ name_company: "", region_id: "", unit_id: "" });
                      setUnits([]);
                      setLocations([]);
                    }}
                  >
                    <FiX className="me-1" /> Cancelar
                  </motion.button>
                )}
              </div>
            </div>
          </form>
        </motion.div>

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div className="input-group" style={{ maxWidth: 360 }}>
            <span className="input-group-text bg-white">
              <FiSearch color={iconColor} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Pesquisar empresa..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
          </div>
          {locations.length > 0 && (
            <small style={{ color: "#64748b" }}>Locais vinculados à unidade selecionada: {locations.length}</small>
          )}
        </div>

        <div ref={tableRef} className="modern-card p-2 p-md-3">
          <DataTable
            columns={tableColumns}
            data={filteredItems}
            progressPending={pending}
            pagination
            highlightOnHover
            pointerOnHover
            responsive
            striped
            dense
            noHeader
            customStyles={{
              rows: {
                style: {
                  minHeight: "58px",
                  borderBottom: "1px solid rgba(148,163,184,0.18)",
                  fontSize: "0.92rem",
                },
              },
              headCells: {
                style: {
                  background: "#f8fbff",
                  color: "#334155",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                },
              },
              pagination: {
                style: {
                  borderTop: "1px solid rgba(148,163,184,0.2)",
                  minHeight: "56px",
                },
              },
            }}
          />
        </div>
      </motion.div>
    </Layout>
  );
}

export default CompanyPage;
